import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { db, auth } from '../firebase';
import { toast } from 'react-toastify';

function useAgenda() {
  const [eventos, setEventos] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [novoEvento, setNovoEvento] = useState({
    tipo: 'Consulta',
    descricao: '',
    data: '',
    hora: '',
    nomePaciente: '',
    emailPaciente: '',
    telefonePaciente: '',
  });
  const [error] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [eventoToRemove, setEventoToRemove] = useState(null);
  const [user] = useAuthState(auth);

  useEffect(() => {
    if (user) {
      fetchEventsFromFirebase();
      fetchPacientes();
    }
  }, [user]);

  const fetchEventsFromFirebase = async () => {
    if (user) {
      try {
        const q = query(
          collection(db, 'agenda'),
          where('userId', '==', user.uid)
        );
        const snapshot = await getDocs(q);
        setEventos(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        toast.error('Erro ao buscar eventos.');
      }
    }
  };

  const fetchPacientes = async () => {
    if (user) {
      try {
        const q = query(
          collection(db, 'pacientes'),
          where('userId', '==', user.uid)
        );
        const snapshot = await getDocs(q);
        setPacientes(
          snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        );
      } catch (error) {
        toast.error('Erro ao buscar pacientes.');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNovoEvento((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePacienteSelect = (event, selectedPaciente) => {
    if (typeof selectedPaciente === 'string') {
      // Caso o usuário tenha digitado manualmente o nome do paciente
      setNovoEvento((prev) => ({
        ...prev,
        nomePaciente: selectedPaciente,
        emailPaciente: '', // Resetar os outros campos
        telefonePaciente: '',
      }));
    } else if (selectedPaciente && typeof selectedPaciente === 'object') {
      // Caso o paciente tenha sido selecionado do autocomplete
      setNovoEvento((prev) => ({
        ...prev,
        nomePaciente: selectedPaciente.nome,
        emailPaciente: selectedPaciente.email || '',
        telefonePaciente: selectedPaciente.telefone || '',
      }));
    }
  };

  const adicionarEvento = async () => {
    const camposObrigatoriosComuns = ['tipo', 'descricao', 'data', 'hora'];
    const camposObrigatoriosTeleconsulta = [
      'nomePaciente',
      'emailPaciente',
      'telefonePaciente',
    ];

    // Verificação de campos comuns
    const camposInvalidosComuns = camposObrigatoriosComuns.filter(
      (campo) => !novoEvento[campo]
    );

    if (novoEvento.tipo === 'Teleconsulta') {
      // Verificação adicional de campos para Teleconsulta
      const camposInvalidosTeleconsulta = camposObrigatoriosTeleconsulta.filter(
        (campo) => !novoEvento[campo]
      );
      camposInvalidosComuns.push(...camposInvalidosTeleconsulta);
    }

    // Se houver campos inválidos, exibir erro e parar execução
    if (camposInvalidosComuns.length > 0) {
      toast.error('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    try {
      const dataHora = new Date(`${novoEvento.data}T${novoEvento.hora}`);
      const newEvent = {
        ...novoEvento,
        dataHora,
        userId: user.uid,
      };

      const docRef = await addDoc(collection(db, 'agenda'), newEvent);
      setEventos((prev) => [...prev, { id: docRef.id, ...newEvent }]);
      setNovoEvento({
        tipo: 'Consulta', // ou algum valor padrão
        descricao: '',
        data: '',
        hora: '',
        nomePaciente: '',
        emailPaciente: '',
        telefonePaciente: '',
      });
      toast.success('Evento adicionado com sucesso!');
    } catch {
      toast.error('Erro ao adicionar evento. Tente novamente.');
    }
  };

  const handleRemoveClick = (evento) => {
    if (evento) {
      setEventoToRemove(evento);
      setIsModalOpen(true);
    } else {
      toast.error('Erro: evento não encontrado.');
    }
  };

  const handleConfirmRemove = async () => {
    if (!eventoToRemove || !eventoToRemove.id) {
      toast.error('Erro: nenhum evento selecionado para remoção.');
      return;
    }

    try {
      await deleteDoc(doc(db, 'agenda', eventoToRemove.id));
      setEventos((prev) =>
        prev.filter((evento) => evento.id !== eventoToRemove.id)
      );
      setIsModalOpen(false);
      setEventoToRemove(null);
      toast.success('Evento removido com sucesso!');
    } catch (error) {
      toast.error(`Erro ao remover evento: ${error.message}`);
    }
  };

  const handleCancelRemove = () => {
    setIsModalOpen(false);
    setEventoToRemove(null);
  };

  const handleEditSave = async (updatedEvento) => {
    try {
      const updatedEvent = {
        ...updatedEvento,
        dataHora: new Date(`${updatedEvento.data}T${updatedEvento.hora}`),
      };
      const docRef = doc(db, 'agenda', updatedEvent.id);
      await updateDoc(docRef, {
        data: updatedEvento.data, // Atualizando data
        hora: updatedEvento.hora, // Atualizando hora
      });
      setEventos((prev) =>
        prev.map((evento) =>
          evento.id === updatedEvent.id
            ? { ...evento, data: updatedEvento.data, hora: updatedEvento.hora }
            : evento
        )
      );
      toast.success('Evento atualizado com sucesso!');
    } catch (error) {
      toast.error('Erro ao editar evento. Tente novamente.');
      console.error(error);
    }
  };

  return {
    eventos,
    setEventos, // Certifique-se de que está exportado aqui
    pacientes,
    novoEvento,
    error,
    isModalOpen,
    eventoToRemove,
    setNovoEvento,
    handleInputChange,
    handlePacienteSelect,
    adicionarEvento,
    handleRemoveClick,
    handleConfirmRemove,
    handleCancelRemove,
    handleEditSave,
    fetchEventsFromFirebase,
  };
}

export default useAgenda;
